import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Trash2, Plus, Target, Search, Archive, Copy, CalendarClock, XCircle, Eye } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import type { PracticeAssignment } from '../../../assessment';
import { CreatePracticePanel } from '../components/CreatePracticePanel';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { EmptyState } from '../../../components/shared/EmptyState';
import { DataFreshness } from '../../shared/DataFreshness';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { dataLayer } from '../../../data/dataLayer';
import type { Account, Classroom } from '../../../accounts';
import { containerVariants } from '../shared/animationVariants';
import { genUuid } from '../../../utils/genUuid';
import { toast } from 'sonner';
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
interface HomeworkPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  activeAccount?: Account;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  classroomName?: string;
}
type HomeworkTab = 'open' | 'klaar' | 'archief';
type DerivedStatus = 'open' | 'klaar' | 'verlopen' | 'archief';
interface DerivedRow {
  assignment: PracticeAssignment;
  targetCount: number;
  completedCount: number;
  progressPercent: number;
  derived: DerivedStatus;
}
function deriveStatus(a: PracticeAssignment, targetCount: number, completedCount: number): DerivedStatus {
  if (a.status === 'archived') return 'archief';
  if (completedCount >= targetCount && targetCount > 0) return 'klaar';
  if (a.deadline && Date.now() > a.deadline) return 'verlopen';
  if (a.status === 'completed') return 'klaar';
  return 'open';
}
export function HomeworkPage({
  students,
  selectedGroup,
  activeAccount,
  classrooms = [],
  selectedClassroomId,
  onSelectClassroom,
  classroomName
}: HomeworkPageProps) {
  const [assignments, setAssignments] = useState<PracticeAssignment[]>([]);
  const [tab, setTab] = useState<HomeworkTab>('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: async () => {}
  });
  /*
   `loadError` bestaat omdat een mislukte laadpoging hier eindigde in
   `setAssignments([])` — en dus in exact dezelfde lege staat als "je hebt nog
   geen huiswerk klaargezet". Voor een leerkracht die net iets heeft
   opgegeven, is dat de slechtst mogelijke uitkomst: het scherm zegt dat er
   niets staat, dus hij zet het nog een keer klaar. De leerling krijgt het
   dan dubbel.
   */
  const [loadError, setLoadError] = useState(false);
  const loadData = useCallback(async () => {
    try {
      const data = await dataLayer.getPracticeAssignments();
      const all = Object.values(data);
      const relevant = all.filter(a => (a.classroomId || null) === (selectedClassroomId || null));
      setAssignments(relevant.sort((a, b) => b.createdAt - a.createdAt));
      setLastLoadedAt(new Date());
      setLoadError(false);
    } catch (err) {
      logError('[HomeworkPage] loadData failed:', err);
      // Bewust de bestaande lijst NIET wissen: wat er stond was op een eerder
      // moment echt zo, en dat is een betere benadering dan "leeg".
      setLoadError(true);
    }
  }, [selectedClassroomId]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  const derivedRows: DerivedRow[] = useMemo(() => {
    return assignments.map(a => {
      const targetCount = a.wholeClass ? students.length : a.targetStudentIds.length;
      const completedCount = a.completedByStudentIds.length;
      const progressPercent = targetCount > 0 ? Math.round(completedCount / targetCount * 100) : 0;
      return {
        assignment: a,
        targetCount,
        completedCount,
        progressPercent,
        derived: deriveStatus(a, targetCount, completedCount)
      };
    });
  }, [assignments, students.length]);
  const counts = useMemo(() => {
    const c = {
      open: 0,
      klaar: 0,
      archief: 0
    };
    derivedRows.forEach(r => {
      if (r.derived === 'archief') c.archief++;else if (r.derived === 'klaar') c.klaar++;else c.open++; // open + verlopen show under "Open" so teacher can act
    });
    return c;
  }, [derivedRows]);
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return derivedRows.filter(r => {
      // tab filter
      if (tab === 'open') {
        if (r.derived !== 'open' && r.derived !== 'verlopen') return false;
      } else if (tab === 'klaar') {
        if (r.derived !== 'klaar') return false;
      } else {
        if (r.derived !== 'archief') return false;
      }
      // search filter
      if (q && !r.assignment.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [derivedRows, tab, searchQuery]);
  const handleDelete = async (id: string) => {
    // Een bevestiging die alleen "weet je het zeker?" vraagt, verplaatst de
    // beslissing zonder er iets aan toe te voegen — de leerkracht weet nog
    // steeds niet wát hij kwijtraakt. Bij klas verwijderen noemen we de
    // gevolgen wél met aantallen; hier kan er al voortgang van leerlingen aan
    // hangen, dus dat weegt zwaarder, niet lichter.
    const row = derivedRows.find(r => r.assignment.id === id);
    const done = row?.completedCount ?? 0;
    const target = row?.targetCount ?? 0;
    setConfirmAction({
      isOpen: true,
      title: 'Huiswerk verwijderen',
      message: row ? [`"${row.assignment.title}" wordt definitief verwijderd.`, '', `• Klaargezet voor ${target} ${target === 1 ? 'leerling' : 'leerlingen'}`, done > 0 ? `• ${done} ${done === 1 ? 'leerling heeft' : 'leerlingen hebben'} dit al gemaakt — die voortgang verdwijnt mee` : '• Nog niemand heeft eraan gewerkt', '', 'Je kunt dit niet ongedaan maken. Wil je het bewaren maar uit beeld halen, kies dan Archiveren.'].join('\n') : 'Dit huiswerk wordt definitief verwijderd. Je kunt dit niet ongedaan maken.',
      action: async () => {
        // De boolean werd genegeerd: bij een mislukking verdween de rij uit
        // beeld door loadData() en leek het gelukt, tot de volgende sessie.
        const ok = await dataLayer.deletePracticeAssignment(id);
        if (!ok) {
          toast.error('Verwijderen is niet gelukt. Het huiswerk staat er nog; probeer het opnieuw.');
        }
        loadData();
      }
    });
  };
  const handleArchive = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Huiswerk archiveren',
      message: 'Dit huiswerk verdwijnt uit "Open" en gaat naar het archief.',
      action: async () => {
        const ok = await dataLayer.archivePracticeAssignment(id);
        if (!ok) {
          toast.error('Archiveren is niet gelukt. Het huiswerk staat nog open voor je leerlingen.');
        }
        loadData();
      }
    });
  };
  const handleWithdraw = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Huiswerk intrekken',
      message: 'Wil je dit huiswerk intrekken? Leerlingen zien het dan niet meer. Voortgang blijft bewaard.',
      action: async () => {
        // Belangrijker dan bij archiveren: hier denkt de leerkracht dat het
        // huiswerk weg is bij de leerlingen. Blijft het staan, dan maken ze
        // iets wat ingetrokken had moeten zijn.
        const ok = await dataLayer.archivePracticeAssignment(id);
        if (!ok) {
          toast.error('Intrekken is niet gelukt. Je leerlingen zien dit huiswerk nog steeds staan.');
        }
        loadData();
      }
    });
  };
  const handleExtendDeadline = async (assignment: PracticeAssignment) => {
    const current = assignment.deadline ?? Date.now();
    const newDeadline = current + 7 * 24 * 60 * 60 * 1000;
    try {
      const ok = await dataLayer.savePracticeAssignment(assignment.id, {
        ...assignment,
        deadline: newDeadline
      });
      if (!ok) {
        toast.error('De deadline kon niet worden verlengd. De oude datum geldt nog.');
      }
      loadData();
    } catch (err) {
      logError('[HomeworkPage] extend deadline failed:', err);
      toast.error('De deadline kon niet worden verlengd door een fout.');
    }
  };
  const handleDuplicate = async (assignment: PracticeAssignment) => {
    if (!activeAccount) return;
    try {
      // uuid, geen genId: dit id landt in `practice_assignments.id`.
      const newId = genUuid();
      const copy: PracticeAssignment = {
        ...assignment,
        id: newId,
        teacherId: activeAccount.id,
        title: `${assignment.title} (kopie)`,
        createdAt: Date.now(),
        status: 'active',
        completedByStudentIds: []
      };
      const ok = await dataLayer.savePracticeAssignment(newId, copy);
      if (!ok) {
        toast.error('De kopie kon niet worden klaargezet. Probeer het opnieuw.');
      }
      loadData();
    } catch (err) {
      logError('[HomeworkPage] duplicate failed:', err);
      toast.error('De kopie kon niet worden klaargezet door een fout.');
    }
  };
  const handleCreateClose = () => {
    setIsCreateOpen(false);
    loadData();
  };
  const TabButton = ({
    id,
    label,
    count
  }: {
    id: HomeworkTab;
    label: string;
    count: number;
  }) => <button onClick={() => setTab(id)} className={`relative px-5 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 flex items-center gap-2 ${tab === id ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-10128" data-mp="cibg0nrm90jz">
    
      {tab === id && <motion.div layoutId="hwTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
      type: 'spring',
      stiffness: 500,
      damping: 30
    }} data-id="element-10129" data-mp="m83azj3mnvoz" />}
      <span className="relative z-10" data-id="element-10130" data-mp="va47ueuy8fq4">{label}</span>
      <span className={`relative z-10 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${tab === id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-10131" data-mp="108xnactsqgsx">
      
        {count}
      </span>
    </button>;
  return <PageLayout title="Huiswerk" description={classroomName ? `Open, klaar en archief — ${classroomName}` : 'Open, klaar en archief'} data-id="element-10132" data-mp="2ug9khq2a0e5y">
      
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10133" data-mp="2poi3a650fksh">
        
        <div className="flex flex-wrap items-center justify-between gap-3 mb-[-12px]" data-id="element-10134" data-mp="2koc9ajb9pm78">
          {classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId || null} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-10135" data-mp="3dfnjimev4f3b" /> : <span data-id="element-10136" data-mp="38e6kkwfxv2pd" />}
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={loadData} data-id="element-10137" data-mp="33go7ctuw21bk" />
        </div>

        {/* Primary CTA */}
        <motion.div variants={itemVariants} data-id="element-10138" data-mp="2yid1eu7psaym">
          <Card className="p-5 border-l-4 border-l-indigo-400 border-slate-200 shadow-sm bg-white flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-10139" data-mp="2yhlf6a00bpl0">
            <div className="flex-1" data-id="element-10140" data-mp="1n2et4skd1syg">
              <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-10141" data-mp="1n1d8bwdjls3j">
                Huiswerk maken
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-10142" data-mp="f1ekxcm5xw1m">
                Geef leerlingen gericht oefenwerk op voor specifieke vakken of
                onderdelen.
              </p>
            </div>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" size="sm" data-id="element-10143" data-mp="a461l8pa3lys">
              
              <Plus className="w-4 h-4 mr-2" data-id="element-10144" data-mp="3rj30epayd8ta" />
              Huiswerk maken
            </Button>
          </Card>
        </motion.div>

        {/* Tabs */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-10145" data-mp="3ik7ttc3zxwpg">
          
          <div className="bg-slate-100 p-1 rounded-xl inline-flex relative" data-id="element-10146" data-mp="3nju87ovk9izu">
            <TabButton id="open" label="Open" count={counts.open} data-id="element-10147" data-mp="38li8f9cw5u4g" />
            <TabButton id="klaar" label="Klaar" count={counts.klaar} data-id="element-10148" data-mp="3dkv6yrd4kpmv" />
            <TabButton id="archief" label="Archief" count={counts.archief} data-id="element-10149" data-mp="3dk39fazywhty" />
          </div>
          <div className="w-full sm:w-64" data-id="element-10150" data-mp="2tzl62mrvxmqg">
            <Input placeholder="Zoek huiswerk..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-10152" data-mp="33xsbc8ctagcm" />} size="sm" data-id="element-10151" data-mp="2pxr9oqoq34w3" />
            
          </div>
        </motion.div>

        {/* Rows */}
        {filteredRows.length > 0 ? <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10153" data-mp="2ywuuq07g9ud8">
          
            {filteredRows.map(({
          assignment,
          targetCount,
          completedCount,
          progressPercent,
          derived
        }) => {
          const notStarted = targetCount - completedCount;
          return <motion.div key={assignment.id} variants={itemVariants} data-id="element-10154" data-mp="3hpyqbr48ahah">
                    <Card className="p-4 border-slate-200 flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-10155" data-mp="fqyfkshmffl">
                      <div className="flex items-start justify-between mb-3 gap-2" data-id="element-10156" data-mp="3roegnrvflmix">
                        <div className="flex items-start gap-3 min-w-0 flex-1" data-id="element-10157" data-mp="1ozentokzmaoq">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 flex-shrink-0" data-id="element-10158" data-mp="1tyhge9js6oft">
                            <Target className="w-4 h-4" data-id="element-10159" data-mp="1yqrbe3zr6eoy" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-10160" data-mp="23oj24uonvurk">
                            <h3 className="font-bold text-sm text-slate-900 truncate" data-id="element-10161" data-mp="1623o62vg1f8r">
                              {assignment.title}
                            </h3>
                            <div className="text-[10px] text-slate-500 mt-0.5 truncate" data-id="element-10162" data-mp="1fzhs5306kbk6">
                              {assignment.moduleIds.length} vak
                              {assignment.moduleIds.length === 1 ? '' : 'ken'} · {assignment.targetFamilyIds.length}{' '}
                              onderdel
                              {assignment.targetFamilyIds.length === 1 ? '' : 'en'}
                            </div>
                            <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-1 flex-wrap" data-id="element-10163" data-mp="kqnxulbyn5j7">
                              <Calendar className="w-3 h-3 flex-shrink-0" data-id="element-10164" data-mp="fscrwlosdf69" />
                              {new Date(assignment.createdAt).toLocaleDateString('nl-NL')}
                              {assignment.deadline && <>
                                  <span data-id="element-10165" data-mp="618cxlz41ubf">•</span>
                                  <span className={derived === 'verlopen' ? 'text-rose-600 font-bold' : 'text-orange-600'} data-id="element-10166" data-mp="12x1x7x2xdrm">
                                
                                    Deadline:{' '}
                                    {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                                  </span>
                                </>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-10167" data-mp="1mddu9pa5q3xr">
                          {derived === 'open' || derived === 'verlopen' ? <>
                              <button onClick={() => handleExtendDeadline(assignment)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" title="Deadline +7 dagen" aria-label="Deadline verlengen" data-id="element-10168" data-mp="3bvau61aovsd0">
                            
                                <CalendarClock className="w-4 h-4" data-id="element-10169" data-mp="36yu3p0g0p1wr" />
                              </button>
                              <button onClick={() => handleWithdraw(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors" title="Intrekken" aria-label="Huiswerk intrekken" data-id="element-10170" data-mp="3uoohplc2owil">
                            
                                <XCircle className="w-4 h-4" data-id="element-10171" data-mp="3po0iwdhlrbdt" />
                              </button>
                            </> : <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" title="Bekijken" aria-label="Huiswerk bekijken" data-id="element-10172" data-mp="299ioaabqtfkr">
                          
                              <Eye className="w-4 h-4" data-id="element-10173" data-mp="wafg02o2txco" />
                            </button>}
                          <button onClick={() => handleDuplicate(assignment)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" title="Dupliceren" aria-label="Huiswerk dupliceren" data-id="element-10174" data-mp="rd6upt7mtd77">
                          
                            <Copy className="w-4 h-4" data-id="element-10175" data-mp="118qs6k1ugjy2" />
                          </button>
                          {derived !== 'archief' && <button onClick={() => handleArchive(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors" title="Archiveren" aria-label="Archiveren" data-id="element-10176" data-mp="8m617kpa69v1">
                          
                              <Archive className="w-4 h-4" data-id="element-10177" data-mp="hl134klakxsj" />
                            </button>}
                          <button onClick={() => handleDelete(assignment.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" title="Verwijderen" aria-label="Verwijderen" data-id="element-10178" data-mp="3q0unjaiqutev">
                          
                            <Trash2 className="w-4 h-4" data-id="element-10179" data-mp="jp0mdcq3qszu" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-auto space-y-2" data-id="element-10180" data-mp="npc52jywmj8o">
                        <div className="flex items-center justify-between text-xs flex-wrap gap-2" data-id="element-10181" data-mp="sqhzcm87qekb">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-10182" data-mp="xpuudu98mrxz">
                            <span className="text-slate-600 font-medium" data-id="element-10183" data-mp="12nwcocyuan8e">
                              {completedCount} van {targetCount} klaar
                            </span>
                            {notStarted > 0 && (derived === 'open' || derived === 'verlopen') && <span className="inline-flex items-center gap-1 text-amber-700 font-medium text-[11px]" data-id="element-10184" data-mp="1bfyae3hixpez">
                                  · {notStarted} niet begonnen
                                </span>}
                          </div>
                          {derived === 'open' && <Badge variant="default" className="bg-indigo-100 text-indigo-700 text-[10px] py-0 px-1.5" data-id="element-10185" data-mp="1viug6wiao9qp">
                          
                              Loopt nu
                            </Badge>}
                          {derived === 'klaar' && <Badge variant="success" className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5" data-id="element-10186" data-mp="1lmhcd5t1cfi8">
                          
                              Afgerond
                            </Badge>}
                          {derived === 'verlopen' && <Badge variant="danger" className="bg-rose-100 text-rose-700 text-[10px] py-0 px-1.5" data-id="element-10187" data-mp="299pgvlin3kj7">
                          
                              Deadline voorbij
                            </Badge>}
                          {derived === 'archief' && <Badge variant="default" className="bg-slate-200 text-slate-600 text-[10px] py-0 px-1.5" data-id="element-10188" data-mp="20fkxfn5u5bdp">
                          
                              Gearchiveerd
                            </Badge>}
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10189" data-mp="mojjsdl12d08">
                          <div className={`h-full rounded-full transition-all duration-1000 ${derived === 'klaar' ? 'bg-emerald-500' : derived === 'verlopen' ? 'bg-rose-500' : derived === 'archief' ? 'bg-slate-400' : 'bg-indigo-500'}`} style={{
                    width: `${progressPercent}%`
                  }} data-id="element-10190" data-mp="im6e1p6a6f3g" />
                        
                        </div>
                      </div>
                    </Card>
                  </motion.div>;
        })}
          </motion.div> : <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-10191" data-mp="wmh7mzyz67sb">
          
            {loadError ? <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-id="element-10192" data-mp="1p2i8bf5zfctr">
                <p className="font-bold text-amber-900" data-id="element-10193" data-mp="1kcbl9jr4c0dm">
                  Het huiswerk kon niet worden geladen
                </p>
                <p className="text-sm text-amber-800 leading-relaxed mt-1 max-w-md mx-auto" data-id="element-10194" data-mp="1ro9g632svyku">
                  Er staat dus niet dat er geen huiswerk is — we konden het niet
                  ophalen. Zet niets opnieuw klaar voordat je dit hebt
                  geprobeerd, anders krijgt de klas het dubbel.
                </p>
                <button type="button" onClick={() => void loadData()} className="mt-4 px-4 py-2 rounded-xl border border-amber-300 bg-white text-amber-900 font-semibold hover:bg-amber-100 transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none" data-id="element-10195" data-mp="26ls4537nm7r8">
                  Opnieuw proberen
                </button>
              </div> : <EmptyState title={tab === 'open' ? 'Nog geen open huiswerk' : tab === 'klaar' ? 'Nog niets afgerond' : 'Archief is leeg'} message={tab === 'open' ? 'Zet huiswerk klaar voor je klas of specifieke leerlingen.' : tab === 'klaar' ? 'Zodra een opdracht door alle leerlingen is gemaakt verschijnt deze hier.' : 'Gearchiveerd huiswerk verschijnt hier.'} emotion={tab === 'klaar' ? 'encouraging' : 'thinking'} colorScheme="slate" data-id="element-10196" data-mp="2pbhzbrfbwvjo" />}
          
          </motion.div>}
      </motion.div>

      {activeAccount && <CreatePracticePanel open={isCreateOpen} onClose={handleCreateClose} students={students} selectedGroup={selectedGroup} teacherId={activeAccount.id} classroomId={selectedClassroomId || null} onSuccess={loadData} data-id="element-10197" data-mp="17zgibt2dq337" />}

      <ConfirmDialog open={confirmAction.isOpen} title={confirmAction.title} message={confirmAction.message} confirmLabel="Bevestig" onConfirm={async () => {
      await confirmAction.action();
      setConfirmAction({
        ...confirmAction,
        isOpen: false
      });
    }} onCancel={() => setConfirmAction({
      ...confirmAction,
      isOpen: false
    })} data-id="element-10198" data-mp="1hw37gu1qngb1" />
      
    </PageLayout>;
}